import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import Papa from 'papaparse';
import Fuse from 'fuse.js';
import useDatabase from '../hooks/useDatabase';
import useIsMobile from '../hooks/useIsMobile';
import { DOWNLOAD_CEILING, downloadProgress, neverBackwards } from '../utils/loadingProgress';
import {
  fetchAllCourses,
  fetchCourseData,
  fetchAllCatalogForSearch
} from '../utils/dataUtils';
import { computeDisplayYears, sameYears } from '../utils/displayYears';
import { runMarkers } from '../utils/selectionMarkers';
import { visibleOfferings } from '../utils/formerProfessors';

export const AppContext = createContext();


const EMPTY_LIST = [];

// How long the loading path waits for an animation frame before it goes on
// without one. A hidden tab is served no frames at all, so a yield that only
// ever ends on a frame would hold the whole load until the tab came back.
const PAINT_FRAME_TIMEOUT_MS = 50;

// True when the active selection already holds every relevant value. An empty
// relevant set counts as fully selected: nothing is selectable, so a restore
// would change nothing, the name click deselects, and no markers are drawn.
export const coversAll = (relevant, active) => relevant.every(v => active.includes(v));

// The years and specific semesters the active course can show, after the
// Course Group Selector and the "Include Former Professors" toggle.
const relevantSelection = (activeCourse, courseGroupSelection, includeFormer) => {
  const offerings = activeCourse?.offerings;
  if (!offerings) return { years: EMPTY_LIST, semesters: EMPTY_LIST };

  const catalog = activeCourse.catalog || EMPTY_LIST;
  const selectedIds = new Set(
    catalog.filter(c => courseGroupSelection[c.main_catalog_id] !== false)
           .map(c => c.main_catalog_id));
  if (selectedIds.size === 0) return { years: EMPTY_LIST, semesters: EMPTY_LIST };

  const shown = visibleOfferings(offerings, includeFormer);
  const selected = selectedIds.size === catalog.length
    ? shown
    : shown.filter(o => selectedIds.has(o.main_catalog_id));

  return {
    years: [...new Set(selected.map(o => o.year))].sort((a, b) => b - a),
    semesters: [...new Set(selected.map(o => o.specific_semester))],
  };
};

export const AppProvider = ({ children }) => {
  const { db, loading: dbLoading, error: downloadError, progress: dbProgress } = useDatabase();
  const [initError, setInitError] = useState(null);
  // The download is only the first half of the wait. A failure in either half
  // leaves the reader with nothing to look at, so both are surfaced as one
  // error: the escape from the loading screen and the bar on the information
  // page then release together, whichever half failed.
  const dbError = downloadError || initError;
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [semesterMapping, setSemesterMapping] = useState(EMPTY_LIST);
  const [appLoading, setAppLoading] = useState(true);

  // All Courses state
  const [allCourses, setAllCourses] = useState(EMPTY_LIST);
  const [mainCourseMap, setMainCourseMap] = useState(new Map());
  const [filteredCourses, setFilteredCourses] = useState(EMPTY_LIST);
  const [fuse, setFuse] = useState(null);

  // Pinned Courses
  const [pinnedCourses, setPinnedCourses] = useState(EMPTY_LIST);

  // Course Display 1 State
  const [coursesInDisplay1, setCoursesInDisplay1] = useState(EMPTY_LIST);

  // Active Course State (for Display 2)
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeYears, setActiveYears] = useState(EMPTY_LIST);
  const [activeSemesters, setActiveSemesters] = useState(EMPTY_LIST);

  // UI Toggles
  const [autoPin, setAutoPin] = useState(true);
  const [showCourseGroups, setShowCourseGroups] = useState(false);
  const [granularView, setGranularView] = useState(false);
  const [showAllYears, setShowAllYears] = useState(true);
  const [showCourseCount, setShowCourseCount] = useState(true);
  // "Include Former Professors". The mobile layout has no toggle, so it always
  // shows them.
  const [includeFormerSetting, setIncludeFormerProfessors] = useState(true);
  const isMobile = useIsMobile(900);
  const includeFormerProfessors = includeFormerSetting || isMobile;
  // The toggle value the year and semester selection was made for.
  const [selectionIncludesFormer, setSelectionIncludesFormer] = useState(includeFormerProfessors);

  // Course Group Selector State
  const [courseGroupSelection, setCourseGroupSelection] = useState({});

  // The year columns of Course Display 1, computed once for the whole display.
  const [displayYears, setDisplayYears] = useState(EMPTY_LIST);

  // URL State Hydration
  const [initializationDone, setInitializationDone] = useState(false);

  // When the wait began, which is what the download projects against.
  const waitStartedAt = useRef(performance.now());

  // The bar only ever moves forward. Each stage reports where it has got to,
  // and a stage that finishes sooner than the one before it cannot pull the
  // bar backwards.
  const advanceLoading = useCallback((percent, message) => {
    setLoadingProgress((current) => neverBackwards(current, percent));
    if (message) setLoadingMessage(message);
  }, []);

  // The same, for the stages that are about to take the main thread. A state
  // update made outside an event handler is scheduled rather than applied, so
  // the browser can reach its next frame with the old value still in the
  // document and the reader never sees the new one at all. `flushSync` writes
  // it now; the animation frame and the task behind it give the browser its
  // chance to draw before the caller blocks.
  const paintLoading = useCallback(async (percent, message) => {
    // The first caller is reached from an effect body, so React is still
    // inside its commit, where flushSync does nothing at all and says so on
    // the console. One turn of the microtask queue puts this outside it.
    await Promise.resolve();
    flushSync(() => {
      setLoadingProgress((current) => neverBackwards(current, percent));
      if (message) setLoadingMessage(message);
    });
    // The frame is a courtesy to a tab that can draw. A hidden tab is served
    // no frames and its timers are clamped to one a second or slower, so
    // waiting there buys nothing and costs the reader the whole clamp: the
    // value is in the document already, so the yield is simply skipped.
    if (typeof document !== 'undefined' && document.hidden) return;
    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      requestAnimationFrame(() => setTimeout(finish, 0));
      setTimeout(finish, PAINT_FRAME_TIMEOUT_MS);
    });
  }, []);

  // --- ACTIVE COURSE LOGIC ---
  const activeCourseRun = useRef(0);
  const setAsActiveCourse = useCallback(async (course, year = null, semester = null) => {
    const run = ++activeCourseRun.current;
    if (!db || !course) {
        // No course is active, clear everything
        setActiveCourse(null);
        setActiveYears(EMPTY_LIST);
        setActiveSemesters(EMPTY_LIST);
        return;
    }

    setActiveCourse(course);
    const { catalog, offerings } = await fetchCourseData(db, course.main_course_id);
    if (run !== activeCourseRun.current) return; // a newer activation superseded this one
    setActiveCourse({ ...course, catalog, offerings });

    if (year !== null && semester !== null) {
        // A specific semester bar was clicked
        setActiveYears([year]);
        setActiveSemesters(Array.isArray(semester) ? semester : [semester]);
    } else {
        // General activation (e.g., clicking course info)
        // Enable all relevant years and semesters
        const shown = visibleOfferings(offerings, includeFormerProfessors);
        setActiveYears([...new Set(shown.map(o => o.year))].sort((a, b) => b - a));
        setActiveSemesters([...new Set(shown.map(o => o.specific_semester))]);
        setSelectionIncludesFormer(includeFormerProfessors);
    }
  }, [db, includeFormerProfessors]);

  // --- DATA LOADING ---
  useEffect(() => {
    // Load semester_mapping.csv
    Papa.parse('/semester_mapping.csv', {
      download: true,
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        const sortedMapping = results.data.sort((a, b) => a['Semester Order'] - b['Semester Order']);
        setSemesterMapping(sortedMapping);
      },
    });
  }, []);

  // A download that fails never produces a database, so the work below never
  // runs and never clears the wait. The flag means one thing on both halves of
  // the path - the wait is over - so a failed download ends it here.
  useEffect(() => {
    if (downloadError) setAppLoading(false);
  }, [downloadError]);

  useEffect(() => {
    if (db) {
      const initializeSearch = async () => {
        try {
          // Each of the three stages below holds the main thread. Each is put
          // on the screen before it starts rather than after it finishes, so
          // what the reader is looking at is the stage actually running.
          await paintLoading(DOWNLOAD_CEILING, 'Loading courses...');
          // step fetch courses
          const courses = await fetchAllCourses(db);

          // 1. Fetch all main courses and create a lookup map
          const courseMap = new Map();
          courses.forEach(course => {
            courseMap.set(course.main_course_id, course);
          });
          setAllCourses(courses);
          setFilteredCourses(courses);
          setMainCourseMap(courseMap);

          await paintLoading(DOWNLOAD_CEILING, 'Fetching catalog data...');
          // 2. Fetch all unique catalog entries for searching
          const allCatalogForSearch = await fetchAllCatalogForSearch(db);

          await paintLoading(DOWNLOAD_CEILING, 'Building search index...');
          // 3. Create the combined and pre-processed search index for Fuse.js
          const searchData = new Map();
          const processText = (text) => text.replace(/ - /g, ' ');

          // Add current course name/code combinations
          courses.forEach(c => {
              const searchText = processText(`${c.course_code} ${c.course_name}`);
              searchData.set(c.main_course_id, new Set([searchText]));
          });

          // Add historical course name/code combinations
          allCatalogForSearch.forEach(c => {
              const searchText = processText(`${c.course_code} ${c.course_name}`);
              if (searchData.has(c.main_course_id)) {
                  searchData.get(c.main_course_id).add(searchText);
              } else {
                  searchData.set(c.main_course_id, new Set([searchText]));
              }
          });

          // Convert the map to an array of objects for Fuse
          const searchIndex = Array.from(searchData.entries()).map(([id, strings]) => ({
              main_course_id: id,
              searchStrings: Array.from(strings)
          }));

          const fuseInstance = new Fuse(searchIndex, {
            keys: ['searchStrings'],
            includeScore: true,
            threshold: 0.1, // Stricter search to reduce fuzzy matches
            ignoreLocation: true,
            findAllMatches: true,
          });
          setFuse(fuseInstance);
          advanceLoading(100, 'Ready');
        } catch (error) {
          // The reader must never be left on a loading screen that cannot
          // end. A failure here is surfaced the way a failed download is, so
          // the escape from the loading screen and the bar on the information
          // page release together, and the wait is cleared whatever happened.
          console.error("Failed to prepare the course data:", error);
          setInitError(error instanceof Error ? error : new Error(String(error)));
        } finally {
          setAppLoading(false);
        }
      };
      initializeSearch();
    }
  }, [db, advanceLoading, paintLoading]);

  // --- COURSE DISPLAY 1 YEAR COLUMNS ---
  // One computation for the whole display. Every row reads the result.
  const displayYearsRun = useRef(0);
  useEffect(() => {
    if (!db) return;
    const run = ++displayYearsRun.current;
    const compute = async () => {
      const courseData = await Promise.all(
        coursesInDisplay1.map(c => fetchCourseData(db, c.main_course_id, undefined, includeFormerProfessors))
      );
      if (run !== displayYearsRun.current) return; // a newer run superseded this one
      const years = computeDisplayYears(courseData, showAllYears);
      setDisplayYears(prev => (sameYears(prev, years) ? prev : years));
    };
    compute().catch(e => {
      console.error("Display years computation failed:", e);
    });
  }, [db, showAllYears, coursesInDisplay1, includeFormerProfessors]);

  // --- URL STATE HYDRATION ---
  useEffect(() => {
    if (db && fuse && mainCourseMap.size > 0 && !initializationDone) {
      const params = new URLSearchParams(window.location.search);

      const settingsParam = params.get('settings');
      const pinnedParam = params.get('pinned');
      const coursesParam = params.get('courses');
      const activeParam = params.get('active');

      // 1. Handle settings from URL
      if (settingsParam) {
        const settingsInt = parseInt(settingsParam, 10);
        if (!isNaN(settingsInt)) {
          setAutoPin(!!(settingsInt & 1));
          setShowCourseGroups(!!(settingsInt & 2));
          setShowCourseCount(!!(settingsInt & 4));
          setShowAllYears(!!(settingsInt & 8));
          setGranularView(!!(settingsInt & 16));
          // Set only when the toggle is off, so links made before it existed
          // keep the default.
          setIncludeFormerProfessors(!(settingsInt & 32));
        }
      }

      // 2. Handle pinned courses from URL
      if (pinnedParam) {
        const pinnedIds = pinnedParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
        const coursesToPin = pinnedIds.map(id => mainCourseMap.get(id)).filter(Boolean);
        setPinnedCourses(coursesToPin);
      }

      // 3. Handle courses in display 1 from URL
      if (coursesParam) {
        const courseIds = coursesParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
        const coursesToDisplay = courseIds.map(id => mainCourseMap.get(id)).filter(Boolean);
        setCoursesInDisplay1(coursesToDisplay);
      }

      // 4. Handle active course from URL
      if (activeParam) {
        const activeId = parseInt(activeParam, 10);
        if (!isNaN(activeId)) {
          const courseToActivate = mainCourseMap.get(activeId);
          if (courseToActivate) {
            setAsActiveCourse(courseToActivate);
          }
        }
      }

      setInitializationDone(true);
    }
  }, [db, fuse, mainCourseMap, initializationDone, setAsActiveCourse]);

  // --- SEARCH LOGIC ---
  const handleSearch = useCallback((term) => {
    if (!term) {
      setFilteredCourses(allCourses);
      return;
    }
    if (fuse) {
      const processedTerm = term.replace(/ - /g, ' ');
      const results = fuse.search(processedTerm);

      // Get unique main_course_ids from results
      const uniqueMainCourseIds = [...new Set(results.map(result => result.item.main_course_id))];

      // Map back to full MainCourse objects
      const matchedCourses = uniqueMainCourseIds
        .map(id => mainCourseMap.get(id))
        .filter(Boolean); // filter(Boolean) removes any undefined if a course isn't found

      setFilteredCourses(matchedCourses);
    }
  }, [fuse, allCourses, mainCourseMap]);


  // --- COURSE DISPLAY 1 LOGIC ---
  const addCourseToDisplay1 = useCallback((course) => {
    if (coursesInDisplay1.some(c => c.main_course_id === course.main_course_id)) return;
    setCoursesInDisplay1(prev => [...prev, course]);
    if (autoPin) {
      setPinnedCourses(prev =>
        prev.some(p => p.main_course_id === course.main_course_id) ? prev : [...prev, course]);
    }
    setAsActiveCourse(course);
  }, [coursesInDisplay1, autoPin, setAsActiveCourse]);

  const removeCourseFromDisplay1 = useCallback((courseId) => {
    setCoursesInDisplay1(prev => prev.filter(c => c.main_course_id !== courseId));
    if (activeCourse && activeCourse.main_course_id === courseId) {
      // The filters belong to the course that just left the display.
      setActiveCourse(null);
      setActiveYears(EMPTY_LIST);
      setActiveSemesters(EMPTY_LIST);
    }
  }, [activeCourse]);

  const reorderCoursesInDisplay1 = useCallback((dragIndex, hoverIndex) => {
    setCoursesInDisplay1(prev => {
      const next = [...prev];
      const [dragged] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, dragged);
      return next;
    });
  }, []);


  // --- PINNING LOGIC ---
  const togglePin = useCallback((course) => {
    setPinnedCourses(prev => {
      const isPinned = prev.find(p => p.main_course_id === course.main_course_id);
      if (isPinned) {
        return prev.filter(p => p.main_course_id !== course.main_course_id);
      } else {
        return [...prev, course];
      }
    });
  }, []);

  // --- FULL SELECTION FOR THE ACTIVE COURSE ---
  // YearSpecifier, SemesterSpecifier, the Course Display 1 selection markers
  // and the course-name click all read these.
  const { years: allRelevantYears, semesters: allRelevantSemesters } = useMemo(
    () => relevantSelection(activeCourse, courseGroupSelection, includeFormerProfessors),
    [activeCourse, courseGroupSelection, includeFormerProfessors]);

  const yearsAllSelected = coversAll(allRelevantYears, activeYears);
  const semestersAllSelected = coversAll(allRelevantSemesters, activeSemesters);

  // A full selection stays full when the effective toggle adds or removes years
  // and semesters, whether the checkbox, a share link or a switch to the mobile
  // layout changed it. A part selection is the reader's own and is left as it
  // is. This runs during the render, so no frame shows the old selection.
  if (selectionIncludesFormer !== includeFormerProfessors) {
    setSelectionIncludesFormer(includeFormerProfessors);
    const before = relevantSelection(activeCourse, courseGroupSelection, selectionIncludesFormer);
    if (activeCourse?.offerings
        && coversAll(before.years, activeYears) && coversAll(before.semesters, activeSemesters)) {
      setActiveYears(allRelevantYears);
      setActiveSemesters(allRelevantSemesters);
    }
  }

  // Which year columns of Course Display 1 carry a selection marker, in the
  // order of `displayYears`. The marker spans the whole column - the header
  // and every course row - so the runs are computed once here rather than per
  // row. A full selection, and an empty selectable set, draw nothing.
  const yearMarkers = useMemo(
    () => runMarkers(displayYears, year => !yearsAllSelected && activeYears.includes(year)),
    [displayYears, yearsAllSelected, activeYears]);

  // Put every year and semester of the active course back on.
  const restoreFullSelection = useCallback(() => {
    setActiveYears(allRelevantYears);
    setActiveSemesters(allRelevantSemesters);
  }, [allRelevantYears, allRelevantSemesters]);

  // The download, which is the part of the wait the reader mostly sits
  // through. A proxy that compresses the file would report fewer bytes than
  // arrive, so the share is held at one.
  useEffect(() => {
    if (!dbLoading) return;
    const elapsed = performance.now() - waitStartedAt.current;
    advanceLoading(downloadProgress(dbProgress, elapsed), 'Downloading database...');
  }, [dbLoading, dbProgress, advanceLoading]);

  // The database arriving means it has been opened as well: sql.js parses it
  // before the handle exists. So this moves the bar and leaves the naming of
  // the stage to the work that follows, which is the work actually running.
  useEffect(() => {
    if (db) advanceLoading(DOWNLOAD_CEILING);
  }, [db, advanceLoading]);

  const value = useMemo(() => ({
    db,
    dbLoading,
    dbError,
    dbProgress,
    loadingProgress,
    loadingMessage,
    semesterMapping,
    allCourses,
    filteredCourses,
    handleSearch,
    pinnedCourses,
    togglePin,
    coursesInDisplay1,
    addCourseToDisplay1,
    removeCourseFromDisplay1,
    reorderCoursesInDisplay1,
    displayYears,
    activeCourse,
    setAsActiveCourse,
    activeYears,
    setActiveYears,
    activeSemesters,
    setActiveSemesters,
    allRelevantYears,
    allRelevantSemesters,
    yearsAllSelected,
    yearMarkers,
    semestersAllSelected,
    restoreFullSelection,
    autoPin,
    setAutoPin,
    showCourseGroups,
    setShowCourseGroups,
    granularView,
    setGranularView,
    showAllYears,
    setShowAllYears,

    showCourseCount,
    setShowCourseCount,
    includeFormerProfessors,
    setIncludeFormerProfessors,
    courseGroupSelection,
    setCourseGroupSelection,
    appLoading,
  }), [
    db, dbLoading, dbError, dbProgress, loadingProgress, loadingMessage, semesterMapping,
    allCourses, filteredCourses, handleSearch, pinnedCourses, togglePin,
    coursesInDisplay1, addCourseToDisplay1, removeCourseFromDisplay1,
    reorderCoursesInDisplay1, displayYears, activeCourse, setAsActiveCourse,
    activeYears, activeSemesters, allRelevantYears, allRelevantSemesters,
    yearsAllSelected, semestersAllSelected, yearMarkers, restoreFullSelection,
    autoPin, showCourseGroups, granularView, showAllYears, showCourseCount,
    includeFormerProfessors, setIncludeFormerProfessors, courseGroupSelection, appLoading,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
